<template>
  <div class="visitor-settings">
    <!-- Background Image with blur & transparency -->
    <div class="background-image"></div>

    <b-container fluid>
      <b-row>
        <b-col>
          <div class="page-header">
            <h3 class="page-title">
              <b-icon icon="gear" class="title-icon" />
              Visitor System Settings
            </h3>
            <p class="page-subtitle">Manage employee face enrollment and system configuration</p>
          </div>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col md="6">
          <b-card class="glass-card form-card">
            <template #header>
              <h5 class="card-header-title">
                <b-icon icon="person-plus" class="header-icon" />
                Enroll Employee Face
              </h5>
            </template>
            
            <b-form @submit.prevent="enrollEmployee">
              <b-form-group label="Select Employee" class="form-group-custom">
                <b-form-select 
                  v-model="selectedEmployee" 
                  :options="employeeOptions"
                  class="form-select-glass"
                />
              </b-form-group>
              
              <b-form-group label="Face Images (5 required)" class="form-group-custom">
                <div class="file-upload-wrapper">
                  <b-form-file
                    v-model="faceImages"
                    multiple
                    accept="image/*"
                    placeholder="Choose 5 face images..."
                    @change="handleImageUpload"
                    class="form-file-glass"
                  />
                  <small class="text-muted upload-hint">
                    <b-icon icon="info-circle" /> Upload 5 different images of the employee's face
                  </small>
                </div>
              </b-form-group>
              
              <div v-if="imagePreviews.length > 0" class="mt-3 image-preview-grid">
                <p class="preview-label">
                  <b-icon icon="images" /> {{ imagePreviews.length }}/5 images uploaded
                </p>
                <b-row class="preview-row">
                  <b-col v-for="(img, index) in imagePreviews" :key="index" cols="2" class="preview-col">
                    <div class="preview-wrapper">
                      <img :src="img" class="img-fluid rounded preview-image" />
                      <span class="preview-number">{{ index + 1 }}</span>
                    </div>
                  </b-col>
                </b-row>
              </div>
              
              <b-button 
                type="submit" 
                variant="primary" 
                class="submit-btn"
                :disabled="isEnrolling || imagePreviews.length < 5"
              >
                <b-spinner v-if="isEnrolling" small class="mr-2" />
                <b-icon v-else icon="check2-circle" class="mr-2" />
                {{ isEnrolling ? 'Enrolling...' : 'Enroll Face' }}
              </b-button>
              
              <div v-if="imagePreviews.length > 0 && imagePreviews.length < 5" class="mt-2">
                <b-alert variant="warning" show class="glass-alert">
                  <b-icon icon="exclamation-triangle" /> Need {{ 5 - imagePreviews.length }} more image(s)
                </b-alert>
              </div>
            </b-form>
          </b-card>
        </b-col>
        
        <b-col md="6">
          <b-card class="glass-card table-card">
            <template #header>
              <div class="table-header">
                <h5 class="card-header-title">
                  <b-icon icon="people" class="header-icon" />
                  Enrolled Employees
                </h5>
                <b-badge variant="info" class="enrolled-badge">{{ enrolledEmployees.length }}</b-badge>
              </div>
            </template>
            
            <b-table 
              :items="enrolledEmployees" 
              :fields="employeeFields"
              striped
              hover
              responsive
              class="glass-table"
            >
              <template #cell(has_face)="data">
                <b-badge :variant="data.value ? 'success' : 'danger'" class="status-badge">
                  <b-icon :icon="data.value ? 'check-circle' : 'x-circle'" />
                  {{ data.value ? 'Enrolled' : 'Not Enrolled' }}
                </b-badge>
              </template>
            </b-table>
            
            <div v-if="enrolledEmployees.length === 0" class="text-center py-4 text-muted empty-state">
              <b-icon icon="person-slash" scale="2" class="empty-icon" />
              <p class="mt-2">No employees enrolled yet</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VisitorSettings',
  data() {
    return {
      selectedEmployee: null,
      employeeOptions: [],
      faceImages: [],
      imagePreviews: [],
      isEnrolling: false,
      enrolledEmployees: [],
      employeeFields: [
        { key: 'name', label: 'Name' },
        { key: 'username', label: 'Username' },
        { key: 'has_face', label: 'Face Status' }
      ]
    };
  },
  created() {
    this.loadEmployees();
  },
  methods: {
    async loadEmployees() {
      try {
        const response = await axios.get('/api/visitor/employees/list/', {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });
        
        this.employeeOptions = response.data.map(emp => ({
          value: emp.id,
          text: `${emp.name} ${emp.has_face ? '✓' : ''}`
        }));
        
        this.enrolledEmployees = response.data;
      } catch (error) {
        console.error('Failed to load employees', error);
        this.$bvToast.toast('Failed to load employees', { variant: 'danger' });
      }
    },
    
    handleImageUpload(event) {
      const files = event.target.files;
      this.imagePreviews = [];
      
      for (let i = 0; i < Math.min(files.length, 5); i++) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreviews.push(e.target.result);
        };
        reader.readAsDataURL(files[i]);
      }
    },
    
    async enrollEmployee() {
      if (!this.selectedEmployee) {
        this.$bvToast.toast('Please select an employee', { variant: 'warning' });
        return;
      }
      
      if (this.imagePreviews.length < 5) {
        this.$bvToast.toast('Please upload 5 face images', { variant: 'warning' });
        return;
      }
      
      this.isEnrolling = true;
      
      try {
        const response = await axios.post('/api/visitor/employees/enroll/', {
          user_id: this.selectedEmployee,
          images: this.imagePreviews
        }, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });
        
        if (response.data.success) {
          this.$bvToast.toast('Face enrolled successfully!', { variant: 'success' });
          this.imagePreviews = [];
          this.faceImages = [];
          this.selectedEmployee = null;
          this.loadEmployees();
        } else {
          this.$bvToast.toast(response.data.error || 'Enrollment failed', { variant: 'danger' });
        }
      } catch (error) {
        console.error('Enrollment failed', error);
        this.$bvToast.toast('Enrollment failed', { variant: 'danger' });
      } finally {
        this.isEnrolling = false;
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   BACKGROUND IMAGE (blurred, transparent)
   ============================================ */
.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(8px);
  opacity: 0.7;
  z-index: -1;
  transform: scale(1.1);
  transition: filter 0.3s ease;
}

/* ============================================
   PAGE
   ============================================ */
.visitor-settings {
  padding: 24px;
  min-height: 100vh;
  position: relative;
}

/* ============================================
   PAGE HEADER
   ============================================ */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-weight: 700;
  color: #0b1a30;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  text-shadow: 0 1px 4px rgba(255, 255, 255, 0.4);
}

.title-icon {
  color: #4a6cf7;
}

.page-subtitle {
  color: #1f2a3e;
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(255, 255, 255, 0.5);
}

/* ============================================
   GLASS CARDS
   ============================================ */
.glass-card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.25);
  overflow: hidden;
  height: 100%;
}

.glass-card >>> .card-header {
  background: rgba(244, 247, 252, 0.5);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
  padding: 14px 24px;
}

.glass-card >>> .card-body {
  padding: 24px;
  background: transparent !important;
}

.card-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #0b1a30;
  margin: 0;
  font-size: 17px;
}

.header-icon {
  color: #4a6cf7;
}

/* ============================================
   FORM
   ============================================ */
.form-card {
  margin-bottom: 0;
}

.form-group-custom {
  margin-bottom: 20px;
}

.form-group-custom >>> label {
  font-weight: 600;
  color: #1f2a3e;
  font-size: 14px;
}

.form-select-glass {
  border: 1px solid rgba(208, 213, 221, 0.5);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
  color: #0b1a30;
}

.form-select-glass:focus {
  border-color: #4a6cf7;
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.15);
  background: rgba(255, 255, 255, 0.7);
}

/* File Upload */
.file-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-file-glass {
  border: 2px dashed rgba(74, 108, 247, 0.2);
  border-radius: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.form-file-glass:hover {
  border-color: rgba(74, 108, 247, 0.5);
  background: rgba(255, 255, 255, 0.5);
}

.upload-hint {
  font-size: 12px;
  color: #3e5670;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Image Preview */
.image-preview-grid {
  margin-top: 12px;
}

.preview-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2a3e;
  margin-bottom: 8px;
}

.preview-row {
  margin: 0 -4px;
}

.preview-col {
  padding: 0 4px;
}

.preview-wrapper {
  position: relative;
}

.preview-image {
  width: 100%;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid rgba(74, 108, 247, 0.15);
}

.preview-number {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(74, 108, 247, 0.85);
  color: white;
  font-size: 10px;
  font-weight: 700;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

/* Submit Button */
.submit-btn {
  margin-top: 8px;
  padding: 10px 24px;
  font-weight: 700;
  font-size: 15px;
  border-radius: 10px;
  background: #4a6cf7;
  border-color: #4a6cf7;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(74, 108, 247, 0.25);
}

.submit-btn:hover:not(:disabled) {
  background: #3a5cd9;
  border-color: #3a5cd9;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(74, 108, 247, 0.35);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Glass Alert */
.glass-alert {
  border-radius: 8px;
  background: rgba(255, 193, 7, 0.15);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 193, 7, 0.2);
  padding: 8px 12px;
  font-size: 13px;
}

/* ============================================
   TABLE CARD
   ============================================ */
.table-card {
  height: 100%;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.enrolled-badge {
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
}

/* Glass Table */
.glass-table {
  background: transparent !important;
}

.glass-table >>> table {
  background: transparent !important;
}

.glass-table >>> thead th {
  background: rgba(244, 247, 252, 0.4) !important;
  backdrop-filter: blur(4px);
  color: #0b1a30;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
  padding: 10px 14px;
}

.glass-table >>> tbody tr {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(2px);
  transition: background 0.2s ease;
}

.glass-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.4) !important;
}

.glass-table >>> tbody td {
  padding: 10px 14px;
  border-bottom: 1px solid rgba(220, 226, 236, 0.2);
  color: #1f2a3e;
  font-size: 14px;
  vertical-align: middle;
}

.glass-table >>> tbody tr:nth-child(even) {
  background: rgba(248, 250, 255, 0.15) !important;
}

/* Status Badge */
.status-badge {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Empty State */
.empty-state {
  background: transparent !important;
}

.empty-icon {
  color: rgba(74, 108, 247, 0.3);
}

.empty-state p {
  font-size: 15px;
  color: #3e5670;
  font-weight: 500;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 992px) {
  .form-card,
  .table-card {
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .visitor-settings {
    padding: 16px;
  }
  
  .glass-card >>> .card-body {
    padding: 16px;
  }
  
  .glass-card >>> .card-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .preview-image {
    height: 50px;
  }
  
  .preview-col {
    padding: 0 3px;
  }
}

@media (max-width: 576px) {
  .glass-table >>> thead th,
  .glass-table >>> tbody td {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .glass-table >>> thead th {
    font-size: 11px;
  }
  
  .status-badge {
    font-size: 10px;
    padding: 2px 8px;
  }
  
  .preview-image {
    height: 40px;
  }
  
  .preview-number {
    width: 16px;
    height: 16px;
    font-size: 8px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Visitor/Settings.vue